import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { headersFornecedores as headerTable } from './auxiliarFornecedores';
import {
  valoresExibidosFornecedor as valoresExibidos,
  fieldObjectFornecedor as fieldObject,
} from './auxiliarFornecedores';
import BarraInferiorBotoes from '../../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../../components/BarraSubAbas';
import BoxConteudo from '../../../../components/BoxConteudo';
import ContainerConteudo from '../../../../components/ContainerConteudo';
import CustomButton from '../../../../components/CustomButton';
import CustomCol from '../../../../components/CustomCol';
import CustomRow from '../../../../components/CustomRow';
import Input from '../../../../components/InputNew';
import Select from '../../../../components/InputNew/Select';
import Tabela from '../../../../components/Tabela';
import WorkspaceBotoes from '../../../../components/WorkspaceBotoes';
import { getUser } from '../../../../modules/contexts/Auth/RequireAuth';
import { UserContext } from '../../../../modules/contexts/Auth/UserProvider';
import { useFornecedoresCache } from '../../../../services/cache/useFornecedoresCache';
import { useMunicipiosIbgeCache } from '../../../../services/cache/useMunicipiosIbgeCache';
import { useHierarquia } from '../../../../services/enum/useHierarquia';
import { usePorte } from '../../../../services/enum/usePorte';
import { ENTITY } from '../../../../utils/entityNames';
import {
  ExportToExcel,
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
} from '../../../../utils/functions';
import SubAbas from '../subabas';

//Dados do usuário
const user = getUser();
const userID = user.sub;
const entity = ENTITY.ENTITY_Fornecedor;

const FornecedoresIndex: React.FC = () => {
  const { cudFornecedor } = useContext(UserContext);
  const navigate = useNavigate();
  const Hierarquia = useHierarquia();
  const Porte = usePorte();

  //Dados do módulo

  const [ufIndex, setUfIndex] = useState<string>('0');

  //Filtro
  const [formFiltros, setFormFiltros] = useState({
    hierarquia: 0,
    porte: 0,
    uf: '',
    cnpj: '',
    razaoSocial: '',
  });

  const { data: fornecedores, loading: loadingFornecedores } =
    useFornecedoresCache();

  const { data: municipioIbgeData, loading: loadingMunicipios } =
    useMunicipiosIbgeCache();

  const hierarquiaReady = Array.isArray(Hierarquia) && Hierarquia.length > 0;
  const porteReady = Array.isArray(Porte) && Porte.length > 0;
  const tableData = useMemo(() => {
    if (!hierarquiaReady || !porteReady) return [];

    return fornecedores
      .map((registro) => {
        const hierarquiaLabel =
          Hierarquia.find(
            (h) => Number(h.value) === Number(registro.hierarquia),
          )?.label ?? '-';

        const porteLabel =
          Porte.find((p) => Number(p.value) === Number(registro.porte))
            ?.label ?? '-';

        return {
          id: registro.id,
          cnpj: registro.cnpj,
          hierarquia: hierarquiaLabel,
          porte: porteLabel,
          uf: registro.municipioIbge.ufSigla,
          fornecedor: registro.razaoSocial,
        };
      })
      .filter((registro: any) => {
        return (
          (formFiltros.hierarquia === 0 ||
            registro.hierarquia ===
              Hierarquia.find(
                (h) => Number(h.value) === Number(formFiltros.hierarquia),
              )?.label) &&
          (formFiltros.porte === 0 ||
            registro.porte ===
              Porte.find((p) => Number(p.value) === Number(formFiltros.porte))
                ?.label) &&
          (formFiltros.uf === '' ||
            registro.uf.toLowerCase().includes(formFiltros.uf.toLowerCase())) &&
          (formFiltros.cnpj === '' ||
            registro.cnpj
              .toLowerCase()
              .includes(formFiltros.cnpj.toLowerCase())) &&
          (formFiltros.razaoSocial === '' ||
            registro.fornecedor
              .toLowerCase()
              .includes(formFiltros.razaoSocial.toLowerCase()))
        );
      });
  }, [
    fornecedores,
    Hierarquia,
    Porte,
    hierarquiaReady,
    porteReady,
    formFiltros,
  ]);

  const offCanvasData = useMemo(() => {
    if (!hierarquiaReady || !porteReady) return [];

    return fornecedores.map((registro) => {
      const hierarquiaLabel =
        Hierarquia.find((h) => Number(h.value) === Number(registro.hierarquia))
          ?.label ?? '-';

      const porteLabel =
        Porte.find((p) => Number(p.value) === Number(registro.porte))?.label ??
        '-';

      return {
        // Atributos de logs
        id: registro.id,
        dataRegistro:
          formatDate(registro.dataRegistro) +
          ' - ' +
          formataNomeMeioSiglas(registro.usuarioRegistro.nomeCompleto),

        dataUltimaAtualizacao:
          formatDate(registro.dataUltimaAtualizacao) +
          ' - ' +
          formataNomeMeioSiglas(registro.usuarioAtualizacao.nomeCompleto),

        dataUltimaVisualizacao:
          formatDate(registro.dataUltimaVisualizacao) +
          ' - ' +
          formataNomeMeioSiglas(
            registro.usuarioUltimaVisualizacao.nomeCompleto,
          ),

        nEdicoes: registro.nEdicoes,
        nVisualizacoes: registro.nVisualizacoes,

        separadorLog: '',
        cnpj: <b>{registro.cnpj}</b>,
        razaoSocial: <b>{registro.razaoSocial}</b>,
        nomeFantasia: registro.nomeFantasia,

        hierarquia: hierarquiaLabel,
        porte: porteLabel,

        dataAbertura: formatDateSimp(registro.dataAbertura),

        separadorContato: '',
        telefone1: registro.telefone1,
        telefone2: registro.telefone2,
        email: registro.email,
        site: registro.site,

        separadorCEP: '',
        cep: registro.cep,
        bairro: registro.bairro,
        logradouro: registro.logradouro,
        numeroImovel: registro.numeroImovel,
      };
    });
  }, [fornecedores, Hierarquia, Porte, hierarquiaReady, porteReady]);

  const ufOptions = [
    { value: 0, label: ' - ' },
    ...municipioIbgeData
      .filter(
        (item, index, self) =>
          // Filtra apenas os itens cujas labels são únicas
          index === self.findIndex((i) => i.ufSigla === item.ufSigla),
      )
      .sort((a, b) => a.ufSigla.localeCompare(b.ufSigla))
      .map((item, index) => ({
        value: index + 1,
        label: item.ufSigla,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  //Funções Handle
  const handleInserir = () => {
    navigate('dadosgerais');
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const newValue = parseInt(value, 10);
    if (name === 'hierarquia') {
      setFormFiltros({
        ...formFiltros,
        hierarquia: newValue,
      });
    } else if (name === 'uf') {
      const uf = ufOptions.find((option) => option.value.toString() === value);
      setUfIndex(value);
      if (uf) {
        setFormFiltros({
          ...formFiltros,
          uf: uf.label,
        });
      }
    } else {
      setFormFiltros((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClearFiltros = () => {
    setFormFiltros({
      ...formFiltros,
      hierarquia: 0,
      porte: 0,
      uf: '',
      cnpj: '',
      razaoSocial: '',
    });
    setUfIndex('0');
  };

  return (
    <>
      <BarraSubAbas subAbas={SubAbas} />

      <ContainerConteudo>
        <BoxConteudo boxTitulo="Filtros" fechado>
          <CustomRow style={{ marginTop: '-15px' }}>
            <CustomCol width="20%">
              <Input
                id="cnpj"
                name="cnpj"
                value={formFiltros.cnpj}
                label="CNPJ"
                onChange={handleChange}
              />
            </CustomCol>

            <CustomCol width="10%">
              <Select
                name="uf"
                value={ufIndex}
                options={ufOptions}
                onChange={handleChange}
                label="UF"
              />
            </CustomCol>
            <CustomCol width="15%">
              <Select
                name="hierarquia"
                value={formFiltros.hierarquia}
                options={Hierarquia}
                onChange={handleChange}
                label="Hierarquia"
              />
            </CustomCol>
            <CustomCol width="15%">
              <Select
                name="porte"
                value={formFiltros.porte}
                options={Porte}
                onChange={handleChange}
                label="Porte"
              />
            </CustomCol>
            <CustomCol width="40%">
              <Input
                id="razaoSocial"
                name="razaoSocial"
                value={formFiltros.razaoSocial}
                label="Fornecedor"
                onChange={handleChange}
              />
            </CustomCol>
          </CustomRow>
          <CustomRow style={{ marginTop: '-15px', textAlign: 'right' }}>
            <CustomCol className="mt-3 justify-content-end align-content-end">
              <CustomButton
                label="Limpar filtros"
                bgColor="#808080" // Cor cinza
                textColor="#ffffff" // Texto branco
                onClick={handleClearFiltros}
              />
            </CustomCol>
          </CustomRow>
        </BoxConteudo>
        <Tabela
          headers={headerTable}
          dataRaw={fornecedores}
          dataTable={tableData}
          displayValues={valoresExibidos}
          dataOffCanvas={offCanvasData}
          tituloCanvas="FORNECEDOR"
        />
      </ContainerConteudo>

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          // download
          // //  downloadDisabled={}
          // onClickDownload={() =>
          //   ExportToExcel(
          //     fornecedores,
          //     valoresExibidos,
          //     fieldObject,
          //     'fornecedores.xlsx',
          //     userID,
          //     'DAF',
          //     entity,
          //   )
          // }
          inserir
          onClickInserir={handleInserir}
          inserirDisabled={!cudFornecedor}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default FornecedoresIndex;
